import React, { useState } from "react";
import { useApp, lineTotal } from "@/context/AppContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag, Scale } from "lucide-react";
import CheckoutDialog from "./CheckoutDialog";

export default function CartDrawer({ open, onOpenChange }) {
  const {
    t,
    cart,
    updateQty,
    removeFromCart,
    subtotal,
    deliveryFee,
    total,
    minOrder,
    meetsMin,
  } = useApp();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const remaining = Math.max(0, minOrder - subtotal);
  const progress = Math.min(100, (subtotal / minOrder) * 100);

  const changeAmount = (item, delta) => {
    if (item.sell_by === "kg") {
      const step = Number(item.step_grams || 50);
      const next = Number(item.grams || 0) + delta * step;
      updateQty(item.id, next);
    } else {
      updateQty(item.id, Number(item.quantity || 0) + delta);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          data-testid="cart-drawer"
          className="w-full sm:max-w-md bg-[#FAF7F2] border-l border-[#E8E2D5] flex flex-col p-0"
        >
          <SheetHeader className="p-6 border-b border-[#E8E2D5]">
            <SheetTitle className="font-heading text-2xl text-[#1B2A22] flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#2E5A36]" />
              {t("cart")}
            </SheetTitle>
            <SheetDescription className="text-[#1B2A22]/60">
              {t("free_delivery_note_dynamic", { min: minOrder.toFixed(0) })}
            </SheetDescription>
          </SheetHeader>

          {cart.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-3">
              <div className="w-20 h-20 rounded-full bg-[#E8E2D5] flex items-center justify-center">
                <ShoppingBag className="w-8 h-8 text-[#2E5A36]" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#1B2A22]">
                {t("cart_empty")}
              </h3>
              <p className="text-sm text-[#1B2A22]/60">{t("cart_empty_desc")}</p>
              <Button
                data-testid="cart-continue-shopping"
                onClick={() => onOpenChange(false)}
                className="mt-4 bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full"
              >
                {t("continue_shopping")}
              </Button>
            </div>
          ) : (
            <>
              {remaining > 0 ? (
                <div className="px-6 py-3 bg-[#D9653B]/10 border-b border-[#E8E2D5]">
                  <p data-testid="min-order-progress" className="text-xs font-semibold text-[#1B2A22] mb-2">
                    {t("free_delivery_progress", {
                      remaining: remaining.toFixed(2),
                    })}
                  </p>
                  <div className="h-2 bg-white rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#D9653B] transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              ) : (
                <div
                  data-testid="free-delivery-active"
                  className="px-6 py-2.5 bg-[#2E5A36]/10 border-b border-[#E8E2D5] flex items-center gap-2"
                >
                  <span className="text-lg">🎁</span>
                  <p className="text-xs font-heading font-bold text-[#2E5A36]">
                    {t("free_delivery_active")}
                  </p>
                </div>
              )}

              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                {cart.map((item) => {
                  const isKg = item.sell_by === "kg";
                  const lt = lineTotal(item);
                  const amountLabel = isKg
                    ? item.grams >= 1000
                      ? `${(item.grams / 1000).toFixed(item.grams % 1000 === 0 ? 0 : 2)} kg`
                      : `${item.grams} gr`
                    : `${item.quantity} adet`;
                  return (
                    <div
                      key={item.id}
                      data-testid={`cart-item-${item.id}`}
                      className="flex gap-3 bg-white rounded-xl p-3 border border-[#E8E2D5]"
                    >
                      <div className="w-16 h-16 rounded-lg bg-[#FAF7F2] shrink-0 overflow-hidden relative">
                        {item.image_url && (
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                        {isKg && (
                          <span className="absolute bottom-0 right-0 bg-[#1B2A22] text-[#FAF7F2] text-[9px] font-heading font-bold px-1.5 py-0.5 rounded-tl-md">
                            KG
                          </span>
                        )}
                      </div>
                      <div className="flex-1 flex flex-col gap-1 min-w-0">
                        <h4 className="font-heading font-semibold text-sm text-[#1B2A22] line-clamp-2">
                          {item.name}
                        </h4>
                        <p className="text-[10px] text-[#1B2A22]/60 font-mono">
                          {isKg
                            ? `${amountLabel} × ₺${item.price.toFixed(2)}/kg`
                            : `${amountLabel} × ₺${item.price.toFixed(2)}`}
                        </p>
                        <div className="flex items-center justify-between mt-auto">
                          <span
                            data-testid={`cart-line-total-${item.id}`}
                            className="font-mono font-bold text-[#2E5A36] text-sm"
                          >
                            ₺{lt.toFixed(2)}
                          </span>
                          <div className="flex items-center gap-1 bg-[#FAF7F2] rounded-full p-0.5">
                            <button
                              data-testid={`qty-minus-${item.id}`}
                              onClick={() => changeAmount(item, -1)}
                              className="w-7 h-7 rounded-full bg-white border border-[#E8E2D5] flex items-center justify-center hover:bg-[#E8E2D5]"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span
                              data-testid={`qty-value-${item.id}`}
                              className="min-w-[56px] text-center font-mono font-bold text-xs px-1"
                            >
                              {amountLabel}
                            </span>
                            <button
                              data-testid={`qty-plus-${item.id}`}
                              onClick={() => changeAmount(item, 1)}
                              className="w-7 h-7 rounded-full bg-[#2E5A36] text-[#FAF7F2] flex items-center justify-center hover:bg-[#1B2A22]"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <button
                        data-testid={`remove-item-${item.id}`}
                        onClick={() => removeFromCart(item.id)}
                        className="text-[#1B2A22]/40 hover:text-[#D9653B]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-[#E8E2D5] p-6 bg-white space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-[#1B2A22]/70">{t("subtotal")}</span>
                  <span data-testid="cart-subtotal" className="font-mono font-semibold">
                    ₺{subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#1B2A22]/70">{t("delivery_fee")}</span>
                  <span
                    data-testid="cart-delivery-fee"
                    className={`font-mono font-semibold ${
                      deliveryFee > 0 ? "text-[#D9653B]" : "text-[#2E5A36]"
                    }`}
                  >
                    {deliveryFee > 0 ? `₺${deliveryFee.toFixed(2)}` : t("free")}
                  </span>
                </div>
                <div className="flex justify-between text-lg pt-3 border-t border-[#E8E2D5]">
                  <span className="font-heading font-bold">{t("total")}</span>
                  <span data-testid="cart-total" className="font-mono font-extrabold text-[#2E5A36]">
                    ₺{total.toFixed(2)}
                  </span>
                </div>
                <Button
                  data-testid="checkout-open-btn"
                  onClick={() => setCheckoutOpen(true)}
                  disabled={cart.length === 0}
                  className="w-full h-12 bg-[#D9653B] hover:bg-[#B4522F] text-white font-heading font-bold rounded-full text-base shadow-md disabled:opacity-50"
                >
                  {t("checkout")}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        onSuccess={() => {
          setCheckoutOpen(false);
          onOpenChange(false);
        }}
      />
    </>
  );
}
